/**
 * Entry point for the Notion Word Add-in task pane.
 * Orchestrates initialization and the main import flow.
 * @module taskpane
 */

/* global Office */

import "./taskpane.css";
import { resetCancel, checkCancel, isCancelRequested, getAbortSignal } from "./state";
import { getAuthToken, getSsoToken } from "./office/auth";
import { checkDocumentHasContent, insertContent } from "./office/insert";
import { updateTocWithRetry } from "./office/toc";
import { BACKEND, fetchWithRetry, getAuthHeaders } from "./api/client";
import { prefetchImages, fetchImageBase64 } from "./api/media";
import { setStatus, progress } from "./ui/status";
import { showConfirm } from "./ui/modal";
import {
  changeEmail, openGuide, toggleSbiMetaFields, cancelImport,
  saveOptions, restoreOptions,
} from "./ui/handlers";
import { readFormOptions } from "./ui/form";
import { blocksToHtml } from "./utils/html";
import { splitOnDividers, splitOnHeadings, splitIntoSegments, collectHeadings } from "./utils/blocks";
import { DEFAULT_EMAIL, EMAIL_STORAGE_KEY } from "./config";

// ── Initialisation Office ──────────────────────────
Office.onReady(async (info) => {
  const saved = localStorage.getItem(EMAIL_STORAGE_KEY) || DEFAULT_EMAIL;
  document.getElementById("email-input").value = saved;

  // Restaurer les options sauvegardées
  restoreOptions();

  // Sauvegarder les options quand elles changent
  document.getElementById("toc-check").addEventListener("change", saveOptions);
  document.getElementById("divider-pagebreak-check").addEventListener("change", saveOptions);
  document.getElementById("heading-pagebreak-select").addEventListener("change", saveOptions);
  // sbi-cover-check et sbi-header-check sont gérés par toggleSbiMetaFields qui appelle saveOptions

  // Entrée dans le champ page ID → lance l'import
  document.getElementById("page-id-input").addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      insertPage();
    }
  });

  if (info.host === Office.HostType.Word) {
    const token = await getAuthToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        const email = payload.preferred_username || payload.upn || "";
        if (email) {
          document.getElementById("email-input").value = email;
          document.getElementById("email-input").disabled = true;
          document.querySelector(".btn-change").style.display = "none";
          document.getElementById("sso-indicator").style.display = "block";
        }
      } catch (_) { /* token non décodable, on garde le mode manuel */ }
    }
  }

  if (!getSsoToken()) {
    try {
      const healthRes = await fetch(`${BACKEND}/api/health`);
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        if (healthData.password_required) {
          document.getElementById("password-row").style.display = "block";
        }
      }
    } catch (_) { /* backend pas encore accessible */ }
  }
});

// ── Insertion d'une page Notion dans Word ──────────
async function insertPage() {
  const email = document.getElementById("email-input").value.toLowerCase().trim();
  const pageId = document.getElementById("page-id-input").value.trim();
  const btn = document.getElementById("insert-btn");
  const cancelBtn = document.getElementById("cancel-btn");

  if (!email && !getSsoToken()) { setStatus("Renseigne ton email.", "status-err"); return; }
  if (!pageId) { setStatus("Renseigne un ID de page.", "status-err"); return; }

  localStorage.setItem(EMAIL_STORAGE_KEY, email);
  resetCancel();
  btn.disabled = true;
  btn.textContent = "Chargement...";
  cancelBtn.style.display = "inline-block";
  cancelBtn.disabled = false;
  cancelBtn.textContent = "Annuler";
  progress.show();
  progress.update("Recuperation du contenu", 0, 1);
  setStatus("Verification des droits et recuperation du contenu...", "status-inf");

  try {
    // Rafraîchir le token SSO si disponible
    if (getSsoToken()) await getAuthToken();
    const signal = getAbortSignal();
    const res = await fetchWithRetry(`${BACKEND}/api/page-content/${encodeURIComponent(pageId)}`, {
      headers: getAuthHeaders(),
      signal,
    });

    const blocks = await res.json();
    checkCancel();

    if (!res.ok) {
      setStatus(`Erreur : ${blocks.error}`, "status-err");
      return;
    }

    progress.update("Recuperation du contenu", 1, 1);
    setStatus("Chargement des images...", "status-inf");
    const imgCache = await prefetchImages(blocks, (current, total) =>
      progress.update("Images", current, total)
    );
    checkCancel();

    setStatus("Insertion dans le document...", "status-inf");
    const result = await insertBlocksIntoWord(blocks, imgCache);

    // Si annulé pendant l'insertion, l'UI est déjà réinitialisée par cancelImport
    if (isCancelRequested()) return;

    if (result === "cancelled") {
      setStatus("", "");
    } else if (result === true) {
      setStatus("Page inseree avec succes !", "status-ok");
    } else {
      setStatus("Page inseree. Si le sommaire est vide, fais Ctrl+A puis F9.", "status-inf");
    }

  } catch (err) {
    // Si annulé (flag, __CANCEL__, ou AbortError) → sortir silencieusement
    if (isCancelRequested() || (err && (err.message === "__CANCEL__" || err.name === "AbortError"))) return;

    const details = err && err.message ? ` (${err.message})` : "";
    setStatus(`Erreur pendant l'insertion${details}`, "status-err");
    console.error("Stack:", err.stack || err);
    if (err.debugInfo) console.error("debugInfo:", JSON.stringify(err.debugInfo));
  } finally {
    btn.disabled = false;
    btn.textContent = "Inserer";
    cancelBtn.style.display = "none";
    progress.hide();
  }
}

// ── Orchestration de l'insertion Word ──────────────
async function insertBlocksIntoWord(blocks, imgCache) {
  const hasContent = await checkDocumentHasContent();
  if (hasContent) {
    const ok = await showConfirm("Le document contient du contenu existant. L'insertion va remplacer tout le contenu actuel. Continuer ?");
    if (!ok) return "cancelled";
  }

  checkCancel();

  const options = readFormOptions();
  const { tocEnabled, dividerAsPageBreak, headingBreakMode, sbiEnabled, coverPageEnabled, sbiOptions } = options;

  let groups = dividerAsPageBreak ? splitOnDividers(blocks) : [blocks.filter(b => b.type !== "divider")];
  if (headingBreakMode !== "none") {
    groups = groups.flatMap(g => splitOnHeadings(g, headingBreakMode));
  }
  if (groups.length === 0) return true;

  progress.update("Preparation", 1, 1);
  const headings = collectHeadings(blocks);
  const groupsData = groups.map(g => {
    const segments = splitIntoSegments(g);
    return segments.map(seg => {
      if (seg.type === "html") {
        return { type: "html", html: blocksToHtml(seg.blocks, imgCache, BACKEND) };
      }
      if (seg.type === "image") {
        return { type: "image", block: seg.block, imgCache };
      }
      return seg;
    });
  });

  checkCancel();

  // Pré-charger les logos si nécessaire
  let logoB64 = null;
  let bigLogoB64 = null;
  if (sbiEnabled || coverPageEnabled) {
    try { logoB64 = await fetchImageBase64("assets/sbi-logo.png"); } catch (_) { /* logo indisponible */ }
    if (coverPageEnabled) {
      try { bigLogoB64 = await fetchImageBase64("assets/sbi-big-logo.png"); } catch (_) { /* logo indisponible */ }
    }
  }

  try {
    await insertContent({
      groupsData,
      headings,
      tocEnabled,
      coverPageEnabled,
      sbiEnabled,
      sbiOptions,
      logoB64,
      bigLogoB64,
      onProgress: (label, current, total) => progress.update(label, current, total),
      checkCancel,
    });
  } catch (err) {
    // Word Online : certaines opérations non critiques throw NotAllowed
    if (err && err.debugInfo && err.debugInfo.code === "NotAllowed") {
      console.warn("Word Online non-critical error:", err.debugInfo.errorLocation, err.message);
    } else {
      throw err;
    }
  }

  // Mise à jour du TOC (si activé)
  if (tocEnabled) {
    progress.update("Mise a jour du sommaire", 1, 1);
    return updateTocWithRetry();
  }
  return true;
}

// ── Exposition globale (requis par webpack) ────────
// Les onclick du HTML ne voient pas le scope du module webpack.
window.insertPage = insertPage;
window.changeEmail = changeEmail;
window.openGuide = openGuide;
window.toggleSbiMetaFields = toggleSbiMetaFields;
window.cancelImport = cancelImport;
